import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import {
  Plus, Edit, Search, AlertTriangle, CheckCircle2, Package,
  TrendingDown, Download, ArrowUpCircle, ArrowDownCircle, Settings, History, Calendar, Trash2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { formatCurrency } from '@/utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CATEGORIES = ["Milling Consumables", "Turning Consumables", "Drilling Consumables"];

const MachiningConsumables = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('');
  const [viewMode, setViewMode] = useState('stock'); // stock, reorder, report
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('description');

  // Dialogs
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [history, setHistory] = useState([]);
  const [report, setReport] = useState(null);
  const [reportMonth, setReportMonth] = useState(() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`; });

  // Forms
  const [itemForm, setItemForm] = useState({
    category: '', hotforge_part_number: '', supplier_part_number: '', description: '',
    grade: '', current_stock: 0, min_stock_level: 0, max_stock_level: 0, cost_per_item: 0, supplier: '', notes: ''
  });
  const [adjForm, setAdjForm] = useState({ quantity: '', adjustment_type: 'stock_added', notes: '' });

  useEffect(() => { fetchAll(); }, [activeCategory, viewMode]);

  const fetchAll = async () => {
    try {
      const params = activeCategory ? `?category=${encodeURIComponent(activeCategory)}` : '';
      const [itemsRes, sumRes] = await Promise.all([
        viewMode === 'reorder'
          ? axios.get(`${BACKEND_URL}/api/machining-consumables/reorder`)
          : axios.get(`${BACKEND_URL}/api/machining-consumables/items${params}`),
        axios.get(`${BACKEND_URL}/api/machining-consumables/summary`)
      ]);
      setItems(itemsRes.data);
      setSummary(sumRes.data);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const handleSaveItem = async () => {
    if (!itemForm.description || !itemForm.category) { toast.error('Description and category required'); return; }
    try {
      if (editingItem) {
        const { current_stock, created_by, ...updateData } = itemForm;
        await axios.put(`${BACKEND_URL}/api/machining-consumables/items/${editingItem.item_id}`, updateData);
        toast.success('Item updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/machining-consumables/items`, { ...itemForm, created_by: user?.email || '' });
        toast.success('Item added');
      }
      setItemDialogOpen(false); setEditingItem(null); fetchAll();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save'); }
  };

  const handleAdjust = async () => {
    if (!selectedItem || !adjForm.quantity) return;
    try {
      await axios.post(`${BACKEND_URL}/api/machining-consumables/items/${selectedItem.item_id}/adjust`, {
        ...adjForm, quantity: parseInt(adjForm.quantity), adjusted_by: user?.email || ''
      });
      toast.success('Stock adjusted');
      setAdjustDialogOpen(false); fetchAll();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to adjust'); }
  };

  const fetchHistory = async (item) => {
    setSelectedItem(item);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/machining-consumables/items/${item.item_id}/history`);
      setHistory(res.data);
      setHistoryDialogOpen(true);
    } catch { toast.error('Failed to load history'); }
  };

  const fetchReport = async (monthStr) => {
    try {
      const [y, m] = monthStr.split('-').map(Number);
      const res = await axios.get(`${BACKEND_URL}/api/machining-consumables/monthly-report?year=${y}&month=${m}`);
      setReport(res.data);
    } catch { toast.error('Failed to load report'); }
  };

  const exportReport = () => {
    if (!report) return;
    const wb = XLSX.utils.book_new();
    const rows = [
      [`Machining Consumables Usage Report — ${report.month_name} ${report.year}`],
      [''], ['Category', 'Item', 'Hotforge P/N', 'Qty Used', 'Cost/Item', 'Total Cost']
    ];
    CATEGORIES.forEach(cat => {
      const catData = report.by_category[cat];
      if (catData?.items?.length > 0) {
        catData.items.forEach(it => rows.push([cat, it.description, it.hotforge_part_number, it.quantity_used, it.cost_per_item, it.total_cost]));
        rows.push(['', '', '', catData.total_qty, 'Subtotal:', catData.total_cost]);
        rows.push([]);
      }
    });
    rows.push(['', '', '', report.grand_total_qty, 'GRAND TOTAL:', report.grand_total_cost]);
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [{ wch: 22 }, { wch: 28 }, { wch: 16 }, { wch: 10 }, { wch: 12 }, { wch: 14 }];
    XLSX.utils.book_append_sheet(wb, ws, 'Usage Report');
    XLSX.writeFile(wb, `Consumables_Usage_${report.month_name}_${report.year}.xlsx`);
    toast.success('Report exported');
  };

  const exportStock = () => {
    const wb = XLSX.utils.book_new();
    const rows = [['Category', 'Hotforge P/N', 'Supplier P/N', 'Description', 'Grade', 'Stock', 'Min', 'Max', 'Cost/Item', 'Stock Value', 'Supplier', 'Status']];
    filteredItems.forEach(i => rows.push([i.category, i.hotforge_part_number, i.supplier_part_number, i.description, i.grade, i.current_stock, i.min_stock_level, i.max_stock_level, i.cost_per_item, i.stock_value, i.supplier, i.stock_status]));
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [{ wch: 20 }, { wch: 14 }, { wch: 14 }, { wch: 28 }, { wch: 10 }, { wch: 8 }, { wch: 6 }, { wch: 6 }, { wch: 10 }, { wch: 12 }, { wch: 16 }, { wch: 10 }];
    XLSX.utils.book_append_sheet(wb, ws, 'Stock');
    XLSX.writeFile(wb, `Consumables_Stock_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success('Stock exported');
  };

  const openAddItem = () => {
    setEditingItem(null);
    setItemForm({ category: activeCategory || '', hotforge_part_number: '', supplier_part_number: '', description: '', grade: '', current_stock: 0, min_stock_level: 0, max_stock_level: 0, cost_per_item: 0, supplier: '', notes: '' });
    setItemDialogOpen(true);
  };

  const openEditItem = (item) => {
    setEditingItem(item);
    setItemForm({ category: item.category, hotforge_part_number: item.hotforge_part_number || '', supplier_part_number: item.supplier_part_number || '', description: item.description, grade: item.grade || '', current_stock: item.current_stock, min_stock_level: item.min_stock_level || 0, max_stock_level: item.max_stock_level || 0, cost_per_item: item.cost_per_item || 0, supplier: item.supplier || '', notes: item.notes || '' });
    setItemDialogOpen(true);
  };

  const openAdjust = (item, type) => {
    setSelectedItem(item);
    setAdjForm({ quantity: '', adjustment_type: type, notes: '' });
    setAdjustDialogOpen(true);
  };

  const handleDeleteItem = async (item) => {
    if (!window.confirm(`Delete "${item.description}" (${item.hotforge_part_number})?\n\nThis will permanently remove this item and cannot be undone.`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/machining-consumables/items/${item.item_id}`);
      toast.success(`${item.description} deleted`);
      fetchAll();
    } catch { toast.error('Failed to delete item'); }
  };

  const filteredItems = items.filter(i => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return i.description?.toLowerCase().includes(q) || i.hotforge_part_number?.toLowerCase().includes(q) || i.supplier_part_number?.toLowerCase().includes(q) || i.grade?.toLowerCase().includes(q) || i.supplier?.toLowerCase().includes(q);
  }).sort((a, b) => {
    if (sortBy === 'current_stock' || sortBy === 'cost_per_item' || sortBy === 'stock_value') {
      return (parseFloat(b[sortBy]) || 0) - (parseFloat(a[sortBy]) || 0);
    }
    return (a[sortBy] || '').toString().toLowerCase().localeCompare((b[sortBy] || '').toString().toLowerCase());
  });

  const statusBadge = (status) => {
    if (status === 'reorder') return <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 font-bold uppercase">Reorder</span>;
    if (status === 'warning') return <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-400 font-bold uppercase">Low</span>;
    return <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/20 text-green-400 font-bold uppercase">OK</span>;
  };

  return (
    <div className="space-y-4">
      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="bg-slate-900/60 border border-slate-800 rounded-lg p-3 text-center">
            <p className="text-[10px] text-slate-500 uppercase">Total Items</p>
            <p className="text-xl font-bold font-mono text-white">{summary.total_items}</p>
          </div>
          <div className="bg-slate-900/60 border border-cyan-500/20 rounded-lg p-3 text-center">
            <p className="text-[10px] text-cyan-400 uppercase">Total Value</p>
            <p className="text-xl font-bold font-mono text-cyan-400">{formatCurrency(summary.total_value)}</p>
          </div>
          <div className="bg-slate-900/60 border border-red-500/20 rounded-lg p-3 text-center cursor-pointer" onClick={() => setViewMode('reorder')}>
            <p className="text-[10px] text-red-400 uppercase">Reorder Required</p>
            <p className="text-xl font-bold font-mono text-red-400">{summary.reorder_count}</p>
          </div>
          {CATEGORIES.slice(0, 2).map(cat => (
            <div key={cat} className="bg-slate-900/60 border border-slate-800 rounded-lg p-3 text-center">
              <p className="text-[10px] text-slate-500 uppercase">{cat.split(' ')[0]}</p>
              <p className="text-sm font-bold font-mono text-white">{summary.by_category[cat]?.count || 0} <span className="text-slate-600 text-xs">items</span></p>
            </div>
          ))}
        </div>
      )}

      {/* Category Selection + Controls */}
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={() => { setActiveCategory(''); setViewMode('stock'); }} className={`text-xs uppercase tracking-wide border ${!activeCategory && viewMode === 'stock' ? 'bg-cyan-500/20 text-cyan-400 border-cyan-500/50' : 'bg-slate-900/40 text-slate-500 border-slate-800'}`} data-testid="cat-all">All</Button>
        {CATEGORIES.map(cat => (
          <Button key={cat} size="sm" onClick={() => { setActiveCategory(cat); setViewMode('stock'); }} className={`text-xs uppercase tracking-wide border ${activeCategory === cat ? 'bg-cyan-500/20 text-cyan-400 border-cyan-500/50' : 'bg-slate-900/40 text-slate-500 border-slate-800'}`} data-testid={`cat-${cat.split(' ')[0].toLowerCase()}`}>
            {cat}
          </Button>
        ))}
        <Button size="sm" onClick={() => setViewMode('reorder')} className={`text-xs uppercase tracking-wide border ${viewMode === 'reorder' ? 'bg-red-500/20 text-red-400 border-red-500/50' : 'bg-slate-900/40 text-slate-500 border-slate-800'}`} data-testid="view-reorder">Reorder Required</Button>
        <div className="ml-auto flex gap-2">
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[150px] h-8 bg-slate-900/60 border-slate-800 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="description">Sort: Description</SelectItem>
              <SelectItem value="hotforge_part_number">Sort: HF P/N</SelectItem>
              <SelectItem value="category">Sort: Category</SelectItem>
              <SelectItem value="grade">Sort: Grade</SelectItem>
              <SelectItem value="supplier">Sort: Supplier</SelectItem>
              <SelectItem value="current_stock">Sort: Stock Qty</SelectItem>
              <SelectItem value="cost_per_item">Sort: Cost</SelectItem>
              <SelectItem value="stock_value">Sort: Value</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" onClick={() => { setReportDialogOpen(true); fetchReport(reportMonth); }} variant="outline" className="border-slate-700 text-xs" data-testid="monthly-report-btn"><Calendar className="w-3.5 h-3.5 mr-1" /> Monthly Report</Button>
          <Button size="sm" onClick={exportStock} variant="outline" className="border-slate-700 text-xs" data-testid="export-stock-btn"><Download className="w-3.5 h-3.5 mr-1" /> Export</Button>
          <Button size="sm" onClick={openAddItem} className="bg-green-500/20 text-green-400 border border-green-500/50 text-xs" data-testid="add-consumable-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add Item</Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search by part number, description, grade, supplier..." className="pl-9 bg-slate-900/60 border-slate-800 text-sm" data-testid="consumable-search" />
      </div>

      {/* Items Table */}
      <div className="bg-slate-900/40 border border-slate-800 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-slate-500 uppercase tracking-wider border-b border-slate-700 bg-slate-900/60">
                <th className="text-left py-2.5 px-3">Status</th>
                <th className="text-left py-2.5 px-3">HF P/N</th>
                <th className="text-left py-2.5 px-3">Description</th>
                <th className="text-left py-2.5 px-3">Grade</th>
                <th className="text-right py-2.5 px-3">Stock</th>
                <th className="text-right py-2.5 px-3">Min</th>
                <th className="text-right py-2.5 px-3">Max</th>
                <th className="text-right py-2.5 px-3">Cost</th>
                <th className="text-right py-2.5 px-3">Value</th>
                <th className="text-left py-2.5 px-3">Supplier</th>
                <th className="text-right py-2.5 px-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/50">
              {filteredItems.map(item => (
                <tr key={item.item_id} className={`hover:bg-slate-800/30 ${item.stock_status === 'reorder' ? 'bg-red-950/10' : ''}`} data-testid={`item-row-${item.item_id}`}>
                  <td className="py-2 px-3">{statusBadge(item.stock_status)}</td>
                  <td className="py-2 px-3 font-mono text-cyan-400">{item.hotforge_part_number || '-'}</td>
                  <td className="py-2 px-3 text-white font-bold">{item.description}</td>
                  <td className="py-2 px-3 text-slate-400">{item.grade || '-'}</td>
                  <td className={`py-2 px-3 text-right font-bold font-mono ${item.stock_status === 'reorder' ? 'text-red-400' : item.stock_status === 'warning' ? 'text-orange-400' : 'text-green-400'}`}>{item.current_stock}</td>
                  <td className="py-2 px-3 text-right text-slate-500 font-mono">{item.min_stock_level}</td>
                  <td className="py-2 px-3 text-right text-slate-500 font-mono">{item.max_stock_level}</td>
                  <td className="py-2 px-3 text-right text-white font-mono">{formatCurrency(item.cost_per_item)}</td>
                  <td className="py-2 px-3 text-right text-cyan-400 font-mono font-bold">{formatCurrency(item.stock_value || 0)}</td>
                  <td className="py-2 px-3 text-slate-400 truncate max-w-[100px]">{item.supplier || '-'}</td>
                  <td className="py-2 px-3">
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" variant="ghost" onClick={() => openAdjust(item, 'stock_added')} className="h-6 w-6 p-0 text-green-400 hover:bg-green-500/10" title="Add Stock" data-testid={`add-stock-${item.item_id}`}><ArrowUpCircle className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => openAdjust(item, 'stock_issued')} className="h-6 w-6 p-0 text-orange-400 hover:bg-orange-500/10" title="Issue Stock" data-testid={`issue-stock-${item.item_id}`}><ArrowDownCircle className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => openEditItem(item)} className="h-6 w-6 p-0 text-cyan-400 hover:bg-cyan-500/10" title="Edit" data-testid={`edit-item-${item.item_id}`}><Edit className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => fetchHistory(item)} className="h-6 w-6 p-0 text-purple-400 hover:bg-purple-500/10" title="History" data-testid={`history-${item.item_id}`}><History className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDeleteItem(item)} className="h-6 w-6 p-0 text-red-400 hover:bg-red-500/10" title="Delete" data-testid={`delete-${item.item_id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredItems.length === 0 && (
                <tr><td colSpan={11} className="text-center py-8 text-slate-600">
                  {viewMode === 'reorder' ? 'No items require reorder' : 'No consumable items found'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ADD/EDIT ITEM DIALOG */}
      <Dialog open={itemDialogOpen} onOpenChange={setItemDialogOpen}>
        <DialogContent className="bg-slate-950 border-cyan-500/30 max-w-lg" data-testid="item-dialog">
          <DialogHeader>
            <DialogTitle className="text-white uppercase tracking-tight flex items-center gap-2">
              <Package className="w-5 h-5 text-cyan-400" />
              {editingItem ? 'Edit Consumable' : 'Add Consumable'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs text-slate-500 uppercase mb-1 block">Category *</Label>
                <Select value={itemForm.category} onValueChange={val => setItemForm(p => ({ ...p, category: val }))}>
                  <SelectTrigger className="bg-slate-900/60 border-slate-800" data-testid="item-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500 uppercase mb-1 block">Hotforge Part No</Label>
                <Input value={itemForm.hotforge_part_number} onChange={e => setItemForm(p => ({ ...p, hotforge_part_number: e.target.value }))} className="bg-slate-900/60 border-slate-800" data-testid="item-hf-pn" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 uppercase mb-1 block">Supplier Part No</Label>
                <Input value={itemForm.supplier_part_number} onChange={e => setItemForm(p => ({ ...p, supplier_part_number: e.target.value }))} className="bg-slate-900/60 border-slate-800" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs text-slate-500 uppercase mb-1 block">Description *</Label>
                <Input value={itemForm.description} onChange={e => setItemForm(p => ({ ...p, description: e.target.value }))} className="bg-slate-900/60 border-slate-800" data-testid="item-description" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 uppercase mb-1 block">Grade</Label>
                <Input value={itemForm.grade} onChange={e => setItemForm(p => ({ ...p, grade: e.target.value }))} className="bg-slate-900/60 border-slate-800" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 uppercase mb-1 block">Supplier</Label>
                <Input value={itemForm.supplier} onChange={e => setItemForm(p => ({ ...p, supplier: e.target.value }))} className="bg-slate-900/60 border-slate-800" />
              </div>
              {!editingItem && (
                <div>
                  <Label className="text-xs text-slate-500 uppercase mb-1 block">Initial Stock</Label>
                  <Input type="number" value={itemForm.current_stock} onChange={e => setItemForm(p => ({ ...p, current_stock: parseInt(e.target.value) || 0 }))} className="bg-slate-900/60 border-slate-800" />
                </div>
              )}
              <div>
                <Label className="text-xs text-slate-500 uppercase mb-1 block">Min Stock Level</Label>
                <Input type="number" value={itemForm.min_stock_level} onChange={e => setItemForm(p => ({ ...p, min_stock_level: parseInt(e.target.value) || 0 }))} className="bg-slate-900/60 border-slate-800" data-testid="item-min-stock" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 uppercase mb-1 block">Max Stock Level</Label>
                <Input type="number" value={itemForm.max_stock_level} onChange={e => setItemForm(p => ({ ...p, max_stock_level: parseInt(e.target.value) || 0 }))} className="bg-slate-900/60 border-slate-800" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 uppercase mb-1 block">Cost per Item (R)</Label>
                <Input type="number" step="0.01" value={itemForm.cost_per_item} onChange={e => setItemForm(p => ({ ...p, cost_per_item: parseFloat(e.target.value) || 0 }))} className="bg-slate-900/60 border-slate-800" data-testid="item-cost" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs text-slate-500 uppercase mb-1 block">Notes</Label>
                <Textarea value={itemForm.notes} onChange={e => setItemForm(p => ({ ...p, notes: e.target.value }))} className="bg-slate-900/60 border-slate-800 min-h-[40px]" />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-3 border-t border-slate-800">
              <Button variant="outline" onClick={() => setItemDialogOpen(false)} className="border-slate-700">Cancel</Button>
              <Button onClick={handleSaveItem} className="bg-cyan-600 hover:bg-cyan-700 text-white font-bold uppercase" data-testid="save-item-btn">{editingItem ? 'Save Changes' : 'Add Item'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* STOCK ADJUSTMENT DIALOG */}
      <Dialog open={adjustDialogOpen} onOpenChange={setAdjustDialogOpen}>
        <DialogContent className="bg-slate-950 border-green-500/30 max-w-sm" data-testid="adjust-dialog">
          <DialogHeader>
            <DialogTitle className="text-white uppercase tracking-tight flex items-center gap-2">
              {adjForm.adjustment_type === 'stock_added' ? <ArrowUpCircle className="w-5 h-5 text-green-400" /> : <ArrowDownCircle className="w-5 h-5 text-orange-400" />}
              {adjForm.adjustment_type === 'stock_added' ? 'Add Stock' : adjForm.adjustment_type === 'stock_issued' ? 'Issue Stock' : 'Manual Correction'}
            </DialogTitle>
          </DialogHeader>
          {selectedItem && (
            <div className="space-y-4">
              <div className="bg-slate-900/60 border border-slate-800 rounded-lg p-3 text-xs">
                <p className="text-white font-bold">{selectedItem.description}</p>
                <p className="text-slate-400">Current stock: <span className="text-white font-bold font-mono">{selectedItem.current_stock}</span></p>
              </div>
              <div>
                <Label className="text-xs text-slate-500 uppercase mb-1 block">Quantity</Label>
                <Input type="number" min="1" value={adjForm.quantity} onChange={e => setAdjForm(p => ({ ...p, quantity: e.target.value }))} className="bg-slate-900/60 border-slate-800 text-white" autoFocus data-testid="adj-quantity" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 uppercase mb-1 block">Adjustment Type</Label>
                <Select value={adjForm.adjustment_type} onValueChange={val => setAdjForm(p => ({ ...p, adjustment_type: val }))}>
                  <SelectTrigger className="bg-slate-900/60 border-slate-800"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="stock_added">Stock Received / Added</SelectItem>
                    <SelectItem value="stock_issued">Stock Issued / Used</SelectItem>
                    <SelectItem value="manual_correction">Manual Correction (set to qty)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500 uppercase mb-1 block">Notes</Label>
                <Input value={adjForm.notes} onChange={e => setAdjForm(p => ({ ...p, notes: e.target.value }))} className="bg-slate-900/60 border-slate-800 text-white" placeholder="e.g. PO number, reason..." />
              </div>
              <div className="flex justify-end gap-3 pt-3 border-t border-slate-800">
                <Button variant="outline" onClick={() => setAdjustDialogOpen(false)} className="border-slate-700">Cancel</Button>
                <Button onClick={handleAdjust} disabled={!adjForm.quantity} className={`font-bold uppercase ${adjForm.adjustment_type === 'stock_added' ? 'bg-green-600 hover:bg-green-700' : 'bg-orange-600 hover:bg-orange-700'} text-white`} data-testid="confirm-adjust-btn">Confirm</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* HISTORY DIALOG */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="bg-slate-950 border-purple-500/30 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white uppercase tracking-tight flex items-center gap-2">
              <History className="w-5 h-5 text-purple-400" /> Transaction History
            </DialogTitle>
          </DialogHeader>
          {selectedItem && <p className="text-xs text-slate-400 mb-3">{selectedItem.description} ({selectedItem.hotforge_part_number})</p>}
          <table className="w-full text-xs">
            <thead><tr className="text-slate-500 uppercase tracking-wider border-b border-slate-700">
              <th className="text-left py-2 px-2">Date</th><th className="text-left py-2 px-2">Type</th><th className="text-right py-2 px-2">Qty</th><th className="text-right py-2 px-2">Prev</th><th className="text-right py-2 px-2">New</th><th className="text-left py-2 px-2">By</th><th className="text-left py-2 px-2">Notes</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-800/50">
              {history.map((h, i) => (
                <tr key={i} className="hover:bg-slate-800/30">
                  <td className="py-1.5 px-2 text-slate-400 font-mono">{h.timestamp?.slice(0, 10)}</td>
                  <td className="py-1.5 px-2"><span className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase ${h.adjustment_type === 'stock_added' ? 'bg-green-500/20 text-green-400' : h.adjustment_type === 'stock_issued' ? 'bg-orange-500/20 text-orange-400' : 'bg-cyan-500/20 text-cyan-400'}`}>{h.adjustment_type?.replace('_', ' ')}</span></td>
                  <td className="py-1.5 px-2 text-right text-white font-bold font-mono">{h.quantity_adjusted}</td>
                  <td className="py-1.5 px-2 text-right text-slate-500 font-mono">{h.previous_stock}</td>
                  <td className="py-1.5 px-2 text-right text-white font-mono">{h.new_stock}</td>
                  <td className="py-1.5 px-2 text-slate-400 truncate max-w-[100px]">{h.adjusted_by || '-'}</td>
                  <td className="py-1.5 px-2 text-slate-500 truncate max-w-[120px]">{h.notes || '-'}</td>
                </tr>
              ))}
              {history.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-slate-600">No transaction history</td></tr>}
            </tbody>
          </table>
        </DialogContent>
      </Dialog>

      {/* MONTHLY REPORT DIALOG */}
      <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
        <DialogContent className="bg-slate-950 border-orange-500/30 max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="monthly-report-dialog">
          <DialogHeader>
            <DialogTitle className="text-white uppercase tracking-tight flex items-center gap-2">
              <Calendar className="w-5 h-5 text-orange-400" /> Monthly Consumables Usage Report
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-between mb-4">
            <input type="month" value={reportMonth} onChange={e => { setReportMonth(e.target.value); fetchReport(e.target.value); }} className="bg-slate-900 border border-slate-700 rounded px-3 py-1.5 text-sm text-white" />
            <Button size="sm" onClick={exportReport} disabled={!report} className="bg-green-500/20 text-green-400 border border-green-500/50" data-testid="export-report-btn"><Download className="w-4 h-4 mr-1" /> Export Excel</Button>
          </div>
          {report && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-orange-950/20 border border-orange-500/20 rounded-lg p-3 text-center">
                  <p className="text-[10px] text-orange-400 uppercase">Total Qty Used</p>
                  <p className="text-2xl font-bold font-mono text-orange-400">{report.grand_total_qty}</p>
                </div>
                <div className="bg-red-950/20 border border-red-500/20 rounded-lg p-3 text-center">
                  <p className="text-[10px] text-red-400 uppercase">Total Cost</p>
                  <p className="text-2xl font-bold font-mono text-red-400">{formatCurrency(report.grand_total_cost)}</p>
                </div>
              </div>
              {CATEGORIES.map(cat => {
                const catData = report.by_category[cat];
                return (
                  <div key={cat} className="border border-slate-800 rounded-lg overflow-hidden">
                    <div className="bg-slate-900/60 px-4 py-2 border-b border-slate-800 flex justify-between items-center">
                      <h4 className="text-sm font-bold text-white uppercase">{cat}</h4>
                      <div className="flex gap-4 text-xs">
                        <span className="text-slate-400">Qty: <span className="text-white font-bold">{catData?.total_qty || 0}</span></span>
                        <span className="text-slate-400">Cost: <span className="text-red-400 font-bold">{formatCurrency(catData?.total_cost || 0)}</span></span>
                      </div>
                    </div>
                    {catData?.items?.length > 0 ? (
                      <table className="w-full text-xs">
                        <thead><tr className="text-slate-500 uppercase tracking-wider border-b border-slate-800">
                          <th className="text-left py-1.5 px-3">Description</th><th className="text-left py-1.5 px-3">HF P/N</th><th className="text-right py-1.5 px-3">Qty Used</th><th className="text-right py-1.5 px-3">Cost/Item</th><th className="text-right py-1.5 px-3">Total Cost</th>
                        </tr></thead>
                        <tbody className="divide-y divide-slate-800/50">
                          {catData.items.map((it, i) => (
                            <tr key={i} className="hover:bg-slate-800/30">
                              <td className="py-1.5 px-3 text-white">{it.description}</td>
                              <td className="py-1.5 px-3 font-mono text-cyan-400">{it.hotforge_part_number}</td>
                              <td className="py-1.5 px-3 text-right text-white font-bold font-mono">{it.quantity_used}</td>
                              <td className="py-1.5 px-3 text-right text-slate-400 font-mono">{formatCurrency(it.cost_per_item)}</td>
                              <td className="py-1.5 px-3 text-right text-red-400 font-bold font-mono">{formatCurrency(it.total_cost)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="px-4 py-3 text-xs text-slate-600 text-center">No usage recorded this month</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MachiningConsumables;
